import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectSearchResultCommand } from '../../../core/mapV2/commands/SelectSearchResultCommand';
import { searchIndexQuery } from '../../../core/mapV2/queries';
import { MIN_QUERY_LENGTH, SearchResult, searchSections } from '../../../core/mapV2/search';
import { useAppDispatch, useAppSelector } from '../../../types';

const keyOf = (result: SearchResult): string =>
    result.kind === 'axis' ? `axis-${result.roadName}` : `section-${result.sectionId}`;

const labelOf = (result: SearchResult): string => (result.kind === 'axis' ? result.roadName : result.label);

// Search box over the map: an axis (selects all its sections) or one section by axis, label or PR.
// Looks in the loaded référentiel, so it is ready as soon as the sections are there.
export const SectionSearch: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const index = useAppSelector(searchIndexQuery);
    const [input, setInput] = useState('');
    const results = useMemo(() => searchSections(index, input), [index, input]);

    const pick = (result: SearchResult | null) => {
        if (!result) {
            return;
        }
        dispatch(
            SelectSearchResultCommand(
                result.kind === 'axis'
                    ? { kind: 'axis', firstSectionId: result.firstSectionId }
                    : { kind: 'section', sectionId: result.sectionId },
            ),
        );
        setInput('');
    };

    return (
        <Autocomplete<SearchResult, false, false, false>
            size="small"
            fullWidth
            options={results}
            value={null}
            inputValue={input}
            onInputChange={(_, value, reason) => reason !== 'reset' && setInput(value)}
            onChange={(_, result) => pick(result)}
            // The results are already the matches of the typed text.
            filterOptions={(options) => options}
            getOptionLabel={labelOf}
            isOptionEqualToValue={(a, b) => keyOf(a) === keyOf(b)}
            noOptionsText={input.trim().length >= MIN_QUERY_LENGTH ? t('mapV2.search.noResults') : ''}
            renderOption={(props, result) => (
                <li {...props} key={keyOf(result)} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                    <span style={{ flex: 1 }}>{labelOf(result)}</span>
                    <span style={{ fontSize: 11, color: '#94A3B8' }}>
                        {result.kind === 'axis'
                            ? `${t('mapV2.search.axis')} · ${t('mapV2.extend.axisHint', { count: result.count })}`
                            : t('mapV2.search.section')}
                    </span>
                </li>
            )}
            renderInput={(params) => {
                // One object: `inputProps` (the native input) and `InputProps` (its wrapper) both need changes.
                const fieldProps = {
                    ...params,
                    placeholder: t('mapV2.map.searchPlaceholder'),
                    inputProps: { ...params.inputProps, 'aria-label': t('mapV2.map.searchPlaceholder') },
                    InputProps: {
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        sx: { height: 36, fontSize: 13 },
                    },
                };

                return <TextField {...fieldProps} />;
            }}
        />
    );
};
